import { create } from 'zustand';
import { RequestStatus } from '@/shared/model/requestStatus';
import { getErrorMessage } from '@/shared/lib/errors';
import { problemAddressService } from '../api/problemAddressService';
import type { ProblemAddressStore } from './types';

/** Хранит актуальные данные очереди и карточки проверки дома */
export const useProblemAddressStore = create<ProblemAddressStore>((set, get) => ({
    list: {
        addresses: [],
        allCount: 0
    },
    current: null,
    listStatus: RequestStatus.Idle,
    detailsStatus: RequestStatus.Idle,
    listError: null,
    detailsError: null,
    listRequestId: 0,
    detailsRequestId: 0,

    /** Загружает список и отбрасывает устаревшие ответы */
    async fetchList(query, signal) {
        const requestId = get().listRequestId + 1;

        set({
            listStatus: RequestStatus.Loading,
            listError: null,
            listRequestId: requestId
        });

        try {
            const list = await problemAddressService.getList(query, signal);

            if (signal?.aborted || requestId !== get().listRequestId) return;

            set({
                list,
                listStatus: RequestStatus.Success
            });
        } catch (error: unknown) {
            if (signal?.aborted || requestId !== get().listRequestId) return;

            set({
                listStatus: RequestStatus.Error,
                listError: getErrorMessage(error, 'Не удалось загрузить адреса')
            });
        }
    },

    /** Загружает карточку выбранного дома */
    async fetchDetails(id, signal) {
        const requestId = get().detailsRequestId + 1;

        set({
            current: null,
            detailsStatus: RequestStatus.Loading,
            detailsError: null,
            detailsRequestId: requestId
        });

        try {
            const current = await problemAddressService.getDetails(id, signal);

            if (signal?.aborted || requestId !== get().detailsRequestId) return;

            set({
                current,
                detailsStatus: RequestStatus.Success
            });
        } catch (error: unknown) {
            if (signal?.aborted || requestId !== get().detailsRequestId) return;

            set({
                detailsStatus: RequestStatus.Error,
                detailsError: getErrorMessage(error, 'Не удалось загрузить адрес')
            });
        }
    },

    /** Обновляет карточку после ручной проверки и сбрасывает очередь */
    setCurrent(point) {
        set({
            current: point,
            detailsStatus: RequestStatus.Success,
            listStatus: RequestStatus.Idle
        });
    },

    /** Сбрасывает данные и делает незавершённые ответы неактуальными */
    reset() {
        set({
            list: {
                addresses: [],
                allCount: 0
            },
            current: null,
            listStatus: RequestStatus.Idle,
            detailsStatus: RequestStatus.Idle,
            listError: null,
            detailsError: null,
            listRequestId: get().listRequestId + 1,
            detailsRequestId: get().detailsRequestId + 1,
        });
    },
}));
